package com.shadowings.gero.data.lessons

import com.shadowings.gero.presentation.model.Word

// Shared by VsoSentenceUtils/AdverbialSentenceUtils/PseudoverbalSentenceUtils
// (and SentenceRepository) — article/genitive formatting and list-joining used
// to build a sentence's translation string, kept in one place instead of
// duplicated per sentence type.

// words.tsv has no proper-noun flag, but proper nouns (gods, kings, people)
// are exactly the human/deity entries that take no article and aren't
// pronouns (is_pronoun).
internal fun Word.isProperNoun(): Boolean =
    isHumanOrDeity && !isPronoun && getArticolo("it").let { it.isNullOrBlank() || it == "-" }

// A common noun for a person or an animal (words.tsv is_human_or_deity /
// is_animal, with an article): "l'uomo", "il gatto", not "Horus" or "io".
internal fun Word.isCommonLivingNoun(): Boolean =
    !isPronoun && (isHumanOrDeity || isAnimal) && getArticolo("it").let { !it.isNullOrBlank() && it != "-" }

// A sentence row's subject a pronoun can stand for: a person or a god
// (words.tsv is_human_or_deity), not a pronoun itself.
internal fun isHumanSubject(id: String, words: List<Word>): Boolean =
    words.any { it.id == id && it.isHumanOrDeity && !it.isPronoun }

// A vocabulary meaning as it reads mid-sentence or inside a prompt. words.tsv
// capitalizes each entry's first letter, so only that letter is lowered:
// proper nouns ("Osiris"), the English pronoun "I", German nouns and inner
// capitals ("king of Lower Egypt") stay as they are. German pronouns are
// lowered like everywhere else ("ich", "er").
internal fun Word.inlineMeaning(languageCode: String, text: String = getMeaning(languageCode)): String = when {
    isProperNoun() -> text
    languageCode == "en" && id == "IO" -> text
    languageCode == "de" && !isPronoun -> text
    else -> text.replaceFirstChar { it.lowercase() }
}

// German separable verbs (USCIRE = "hinausgehen", ENTRARE = "hineingehen"): conjugateVerbDe returns
// "geht hinaus", but in a main clause the particle closes the sentence, after
// objects, complements and "nicht" ("der Priester geht seit gestern nicht
// hinaus"). Applied to every assembled German clause.
private val GERMAN_SEPARABLE_PARTICLES = setOf("hinaus", "hinein")

internal fun moveGermanSeparableParticleToEnd(clause: String): String {
    val tokens = clause.trim().split(" ")
    val index = tokens.indexOfFirst { it in GERMAN_SEPARABLE_PARTICLES }
    if (index == -1 || index == tokens.lastIndex) return clause
    return (tokens.take(index) + tokens.drop(index + 1) + tokens[index]).joinToString(" ")
}

internal fun getCleanMeaning(word: Word, languageCode: String): String {
    val raw = word.getMeaning(languageCode)
    val part = if (raw.contains("/")) raw.split("/")[0].trim() else raw
    return word.inlineMeaning(languageCode, part)
}

internal fun formatItalianSubject(word: Word): String {
    val art = word.getArticolo("it")
    val meaning = getCleanMeaning(word, "it")
    return if (art.isNullOrBlank() || art == "-") meaning
    else if (art.endsWith("'")) "$art$meaning"
    else "$art $meaning"
}

internal fun formatEnglishSubject(word: Word): String {
    val art = word.getArticolo("en") ?: "the"
    val meaning = getCleanMeaning(word, "en")
    return if (art == "-" || art.isBlank()) meaning else "$art $meaning"
}

internal fun formatSpanishSubject(word: Word): String {
    val art = word.getArticolo("es")
    val meaning = getCleanMeaning(word, "es")
    return if (art.isNullOrBlank() || art == "-") meaning else "$art $meaning"
}

// Spanish "a personal" applies not just to people/deities but also to
// animals as direct objects (e.g. "cazan al toro", "el gato caza al pájaro") -
// unlike inanimate objects (bread, water, cities, ...), which never take it.
// animalIsThing: the verb treats its object as food or goods (words.tsv
// es_animal_object_no_a: "come el pez", "ofrece la oca"), so an animal goes without;
// people keep it with every verb ("ofrece al dios").
internal fun needsSpanishPersonalA(word: Word, animalIsThing: Boolean = false): Boolean =
    word.isHumanOrDeity || (word.isAnimal && !animalIsThing)

// The verb's es_animal_object_no_a: every verbs.tsv key is also a words.tsv id.
internal fun animalObjectIsThing(verbKey: String?, words: List<Word>): Boolean =
    verbKey != null && words.any { it.id == verbKey && it.esAnimalObjectNoA }

internal fun formatSpanishObject(word: Word, animalIsThing: Boolean = false): String {
    val art = word.getArticolo("es")
    val meaning = getCleanMeaning(word, "es")
    if (!needsSpanishPersonalA(word, animalIsThing)) {
        return if (art.isNullOrBlank() || art == "-") meaning else "$art $meaning"
    }
    return when {
        art.isNullOrBlank() || art == "-" -> "a $meaning"
        art == "el" -> "al $meaning"
        else -> "a $art $meaning"
    }
}

internal fun formatGermanSubject(word: Word): String {
    val art = word.getArticolo("de")
    val meaning = getCleanMeaning(word, "de")
    return if (art.isNullOrBlank() || art == "-") meaning else "$art $meaning"
}

internal fun formatFrenchSubject(word: Word): String {
    val art = word.getArticolo("fr")
    val meaning = getCleanMeaning(word, "fr")
    return if (art.isNullOrBlank() || art == "-") meaning
    else if (art.endsWith("'")) "$art$meaning"
    else "$art $meaning"
}

internal fun getDemonstrativeGardiner(word: Word): String = when {
    word.isPlural -> "N35:N35"
    word.isFeminine -> "X1:N35"
    else -> "Q3:N35"
}

internal fun getDemonstrativeReading(word: Word): String = when {
    word.isPlural -> "nn"
    word.isFeminine -> "tn"
    else -> "pn"
}

// Gender of a word in a modern language, for agreement (demonstratives).
// isFeminine is the Egyptian gender (it picks the pn/tn demonstrative glyph)
// and often differs: "le cobra", "le temple", "la tombe". Spanish and German
// read it off the words.tsv article; Italian and French use is_feminine_it/_fr
// since "l'" hides it. Words without an article (proper nouns) keep
// isFeminine: a deity's gender doesn't change with the language.
internal fun Word.isFeminineIn(languageCode: String): Boolean {
    val art = getArticolo(languageCode)
    if (art.isNullOrBlank() || art == "-") return isFeminine
    return when (languageCode) {
        "it" -> isFeminineIt
        "fr" -> isFeminineFr
        "es" -> art == "la" || art == "las"
        "de" -> art == "die" && !isPlural
        else -> isFeminine
    }
}

// French elision before a vowel or mute h: "je" + "aime" -> "j'aime".
internal fun startsWithFrenchVowelSound(text: String): Boolean =
    text.firstOrNull()?.lowercaseChar()?.let { it in "aeiouyàâéèêëîïôûœh" } == true

internal fun frenchClause(subject: String, verbPhrase: String): String =
    if (subject == "je" && startsWithFrenchVowelSound(verbPhrase)) "j'$verbPhrase" else "$subject $verbPhrase"

internal fun formatItalianDemonstrativeSubject(word: Word): String {
    val art = word.getArticolo("it")
    val meaning = getCleanMeaning(word, "it")
    val dem = when {
        word.isPlural && word.isFeminineIn("it") -> "queste"
        word.isPlural -> "questi"
        word.isFeminineIn("it") -> if (art?.endsWith("'") == true) "quest'" else "questa"
        else -> if (art?.endsWith("'") == true) "quest'" else "questo"
    }
    return if (dem.endsWith("'")) "$dem$meaning" else "$dem $meaning"
}

internal fun formatEnglishDemonstrativeSubject(word: Word): String {
    val meaning = getCleanMeaning(word, "en")
    val dem = if (word.isPlural) "these" else "this"
    return "$dem $meaning"
}

internal fun formatGermanDemonstrativeSubject(word: Word): String {
    val art = word.getArticolo("de")
    val meaning = getCleanMeaning(word, "de")
    val dem = when {
        word.isPlural -> "diese"
        art == "das" -> "dieses"
        word.isFeminineIn("de") -> "diese"
        else -> "dieser"
    }
    return "$dem $meaning"
}

internal fun formatSpanishDemonstrativeSubject(word: Word): String {
    val meaning = getCleanMeaning(word, "es")
    val dem = when {
        word.isPlural && word.isFeminineIn("es") -> "estas"
        word.isPlural -> "estos"
        word.isFeminineIn("es") -> "esta"
        else -> "este"
    }
    return "$dem $meaning"
}

internal fun formatFrenchDemonstrativeSubject(word: Word): String {
    val art = word.getArticolo("fr")
    val meaning = getCleanMeaning(word, "fr")
    val dem = when {
        word.isPlural -> "ces"
        word.isFeminineIn("fr") -> "cette"
        // "cet" before a vowel sound: the article says so for common nouns
        // ("l'homme" -> "cet homme"), the initial for names ("cet Amenemhat").
        art?.endsWith("'") == true -> "cet"
        (art.isNullOrBlank() || art == "-") && startsWithFrenchVowelSound(meaning) -> "cet"
        else -> "ce"
    }
    return "$dem $meaning"
}

// Accusative/dative/genitive singular of a German noun: only weak nouns
// (words.tsv is_weak_noun_de) change form - "Soldaten", "Hirten", "Adligen".
internal fun germanObliqueSingular(word: Word, noun: String): String = when {
    word.isPlural || !word.isWeakNounDe -> noun
    noun.endsWith("e") -> "${noun}n"
    else -> "${noun}en"
}

// German possessor after its words.tsv genitive article ("des", "der", "von").
// "von" (proper nouns) takes the dative, so the name stays as is ("der
// Weihrauch von Inpu"); feminine and plural nouns don't inflect; weak nouns
// take -(e)n; other masculine/neuter nouns take -s/-es.
internal fun formatGermanGenitivePhrase(genitiveArticle: String, noun: String, word: Word): String {
    if (genitiveArticle.isBlank() || genitiveArticle == "-") return noun
    if (genitiveArticle.trim().split(" ").size > 1) return "$genitiveArticle $noun"
    val inflected = when {
        genitiveArticle.trim() == "von" -> noun
        word.isPlural || word.getArticolo("de") == "die" -> noun
        word.isWeakNounDe -> germanObliqueSingular(word, noun)
        noun.endsWith("es") -> noun
        noun.endsWith("e") || noun.endsWith("el") || noun.endsWith("er") || noun.endsWith("en") -> noun + "s"
        else -> noun + "es"
    }
    return "${genitiveArticle.trim()} $inflected"
}

internal fun formatGermanObject(word: Word): String {
    val art = word.getArticolo("de")
    val meaning = getCleanMeaning(word, "de")
    if (art.isNullOrBlank() || art == "-") return meaning
    return if (!word.isPlural && art == "der") {
        "den ${germanObliqueSingular(word, meaning)}"
    } else {
        "$art $meaning"
    }
}

internal fun joinListIt(items: List<String>): String = when (items.size) {
    0 -> ""
    1 -> items[0]
    2 -> "${items[0]} e ${items[1]}"
    else -> "${items.dropLast(1).joinToString(", ")} e ${items.last()}"
}

internal fun joinListEn(items: List<String>): String = when (items.size) {
    0 -> ""
    1 -> items[0]
    2 -> "${items[0]} and ${items[1]}"
    else -> "${items.dropLast(1).joinToString(", ")} and ${items.last()}"
}

internal fun joinListDe(items: List<String>): String = when (items.size) {
    0 -> ""
    1 -> items[0]
    2 -> "${items[0]} und ${items[1]}"
    else -> "${items.dropLast(1).joinToString(", ")} und ${items.last()}"
}

// Spanish "y" elides to "e" before a word starting with an "i"/"hi" sound
// (but not "hie"/"ia"/"io"/"hia", which don't trigger the elision).
internal fun joinListEs(items: List<String>): String {
    fun conjunctionBefore(next: String): String {
        val n = next.trim().lowercase()
        val startsWithISound =
            n.startsWith("i") && !n.startsWith("hie") && !n.startsWith("ia") && !n.startsWith("io")
        val startsWithHiSound = n.startsWith("hi") && !n.startsWith("hie") && !n.startsWith("hia")
        return if (startsWithISound || startsWithHiSound) "e" else "y"
    }
    return when (items.size) {
        0 -> ""
        1 -> items[0]
        2 -> "${items[0]} ${conjunctionBefore(items[1])} ${items[1]}"
        else -> "${
            items.dropLast(1).joinToString(", ")
        } ${conjunctionBefore(items.last())} ${items.last()}"
    }
}

// French "et" never elides (unlike Spanish "y"/"e").
internal fun joinListFr(items: List<String>): String = when (items.size) {
    0 -> ""
    1 -> items[0]
    2 -> "${items[0]} et ${items[1]}"
    else -> "${items.dropLast(1).joinToString(", ")} et ${items.last()}"
}

internal fun formatItalianComplexObject(possessed: Word, possessor: Word): String {
    val art1 = possessed.getArticolo("it") ?: ""
    val n1 = possessed.inlineMeaning("it", possessed.getMeaning("it").split(",")[0].trim())
    val p1 =
        if (art1.isNotBlank() && art1 != "-") (if (art1.endsWith("'")) "$art1$n1" else "$art1 $n1") else n1
    val gen2 = possessor.getGenitivo("it") ?: ""
    val n2 = possessor.inlineMeaning("it", possessor.getMeaning("it").split(",")[0].trim())
    val p2 =
        if (gen2.isNotBlank() && gen2 != "-") (if (gen2.endsWith("'")) "$gen2$n2" else "$gen2 $n2") else n2
    return "$p1 $p2"
}

internal fun formatEnglishComplexObject(possessed: Word, possessor: Word): String {
    val art1 = possessed.getArticolo("en") ?: ""
    val n1 = possessed.inlineMeaning("en", possessed.getMeaning("en").split(",")[0].trim())
    val p1 = if (art1.isNotBlank() && art1 != "-") "$art1 $n1" else n1
    val gen2 = possessor.getGenitivo("en") ?: ""
    val n2 = possessor.inlineMeaning("en", possessor.getMeaning("en").split(",")[0].trim())
    val p2 = if (gen2.isNotBlank() && gen2 != "-") "$gen2 $n2" else n2
    return "$p1 $p2"
}

internal fun formatSpanishComplexObject(possessed: Word, possessor: Word, animalIsThing: Boolean = false): String {
    val p1 = formatSpanishObject(possessed, animalIsThing)
    val gen2 = possessor.getGenitivo("es") ?: ""
    val n2 = possessor.inlineMeaning("es", possessor.getMeaning("es").split(",")[0].trim())
    val p2 =
        if (gen2.isNotBlank() && gen2 != "-") (if (gen2.endsWith("'")) "$gen2$n2" else "$gen2 $n2") else n2
    return "$p1 $p2"
}

internal fun formatFrenchComplexObject(possessed: Word, possessor: Word): String {
    val art1 = possessed.getArticolo("fr") ?: ""
    val n1 = possessed.inlineMeaning("fr", possessed.getMeaning("fr").split(",")[0].trim())
    val p1 =
        if (art1.isNotBlank() && art1 != "-") (if (art1.endsWith("'")) "$art1$n1" else "$art1 $n1") else n1
    val gen2 = possessor.getGenitivo("fr") ?: ""
    val n2 = possessor.inlineMeaning("fr", possessor.getMeaning("fr").split(",")[0].trim())
    val p2 =
        if (gen2.isNotBlank() && gen2 != "-") (if (gen2.endsWith("'")) "$gen2$n2" else "$gen2 $n2") else n2
    return "$p1 $p2"
}

internal fun formatGermanComplexObject(possessed: Word, possessor: Word): String {
    val p1 = formatGermanObject(possessed)
    val gen2 = possessor.getGenitivo("de") ?: ""
    val n2 = possessor.getMeaning("de").split(",")[0].trim()
    return "$p1 ${formatGermanGenitivePhrase(gen2, n2, possessor)}"
}

internal fun isStativeVerb(verbKey: String, words: List<Word>): Boolean =
    words.firstOrNull { it.id == verbKey }?.isStative == true

internal fun isItForcePresent(verbKey: String, words: List<Word>): Boolean =
    words.firstOrNull { it.id == verbKey }?.itForcePresent == true

internal fun isGermanDirectionalVerb(verbKey: String, words: List<Word>): Boolean =
    words.firstOrNull { it.id == verbKey }?.deDirectional == true

internal fun getStativePastParticipleEn(verbKey: String): String = when (verbKey) {
    "CONOSCERE" -> "known"
    "AMARE" -> "loved"
    "VEDERE" -> "seen"
    "ESISTERE" -> "existed"
    else -> "${verbKey.lowercase()}ed"
}

internal fun formatEnglishObjectPhrase(verbKey: String, objectPhrase: String): String {
    if (objectPhrase.isBlank()) return ""
    val prep = when (verbKey) {
        "GUARDARE" -> "at "
        "PARLARE", "ASCOLTARE" -> "to "
        else -> ""
    }
    return "$prep$objectPhrase"
}
